import { TooltipItem } from 'chart.js'
import { useMemo } from 'react'

import { alpha, useTheme } from '@mui/material'

export interface UAInsightsChartTheme {
  /** Radial/linear axis colours, all derived from `text.primary`. */
  axis: {
    tick: string
    pointLabel: string
    grid: string
    angleLine: string
  }
  /** Alternating very-light row wash behind horizontal bar rows. */
  zebraFill: string
  /** Hovered-row wash matching the dashboard's `action.hover` highlight. */
  rowHighlightFill: string
  /**
   * Shared styling block for Chart.js tooltips (dark surface + circular
   * swatches). Spread into `plugins.tooltip`; charts add their own
   * `mode`/`axis`/`callbacks` on top.
   */
  tooltip: {
    usePointStyle: true
    padding: number
    boxPadding: number
    cornerRadius: number
    backgroundColor: string
    titleColor: string
    titleFont: { size: number; weight: 600 }
    titleMarginBottom: number
    bodyColor: string
    bodyFont: { size: number }
    bodySpacing: number
    borderColor: string
    borderWidth: number
  }
  /** Colours for the tooltip's circular dataset swatches. */
  swatch: {
    /** Solid 1px ring lifting the swatch off the dark tooltip surface. */
    border: string
    /** Used when a dataset colour cannot be resolved to a string. */
    fallback: string
  }
}

/**
 * Theme-derived colour tokens shared by the UA Insights Chart.js charts
 * (motivation bar/radar + comparison radar), so axis/grid/tooltip styling
 * stays consistent and follows the active MUI palette instead of hardcoded
 * hex values.
 */
export const useUAInsightsChartTheme = (): UAInsightsChartTheme => {
  const theme = useTheme()

  return useMemo<UAInsightsChartTheme>(() => {
    const ink = theme.palette.text.primary
    return {
      axis: {
        tick: alpha(ink, 0.55),
        pointLabel: alpha(ink, 0.85),
        grid: alpha(ink, 0.08),
        angleLine: alpha(ink, 0.1),
      },
      zebraFill: alpha(ink, 0.04),
      rowHighlightFill: alpha(ink, 0.08),
      tooltip: {
        usePointStyle: true,
        padding: 12,
        boxPadding: 6,
        cornerRadius: 8,
        // Dark navy rather than Chart.js' default near-black, so the navy
        // "Game & Live Events" dataset swatch stays legible against it.
        // `secondary.dark` is the palette's darkest brand surface, so the
        // tooltip follows the brand navy instead of pinning its own.
        backgroundColor: alpha(theme.palette.secondary.dark, 0.94),
        titleColor: theme.palette.common.white,
        titleFont: { size: 13, weight: 600 },
        titleMarginBottom: 8,
        // Softened white so the body reads as secondary to the title.
        bodyColor: alpha(theme.palette.common.white, 0.88),
        bodyFont: { size: 13 },
        bodySpacing: 6,
        borderColor: alpha(theme.palette.common.white, 0.08),
        borderWidth: 1,
      },
      swatch: {
        border: theme.palette.common.white,
        fallback: theme.palette.grey[500],
      },
    }
  }, [theme])
}

/**
 * Chart.js options shared by the UA Insights radar charts (single-game
 * motivations and the source-vs-target comparison). Both plot the same 0–5
 * intensity scale with the same axis styling and the same tooltip, so they
 * read as one chart type rather than two that happen to look similar.
 */
export const useRadarChartOptions = () => {
  const chartTheme = useUAInsightsChartTheme()

  return useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          min: 0,
          max: 5,
          ticks: { stepSize: 1, backdropColor: 'transparent', font: { size: 11 }, color: chartTheme.axis.tick },
          pointLabels: { font: { size: 11 }, color: chartTheme.axis.pointLabel },
          grid: { color: chartTheme.axis.grid },
          angleLines: { color: chartTheme.axis.angleLine },
        },
      },
      plugins: {
        // Per-chart legend disabled: the page renders a single shared
        // `MotivationSourceLegend` below the charts.
        legend: { display: false },
        tooltip: {
          // Show every visible dataset's value at the hovered motivation so
          // the sources can be compared without chasing each polygon.
          mode: 'index' as const,
          intersect: false,
          // Circular swatches + a calmer dark background so the navy
          // "Game & Live Events" colour stays legible (the default tooltip
          // pinned a tiny white-bordered square that read as empty).
          ...chartTheme.tooltip,
          callbacks: {
            label: (ctx: TooltipItem<'radar'>) => ` ${ctx.dataset.label ?? ''}: ${ctx.parsed.r.toFixed(2)}`,
            labelColor: (ctx: TooltipItem<'radar'>) => {
              // Radar datasets store the solid source colour in `borderColor`
              // (`backgroundColor` is the translucent fill). Coerce to string –
              // the dataset option also allows functions/gradients but we
              // never use those.
              const color = (typeof ctx.dataset.borderColor === 'string' ? ctx.dataset.borderColor : undefined) ?? chartTheme.swatch.fallback
              // Solid 1px white ring lifts the swatch off the dark tooltip
              // surface (the navy "Game & Live Events" dot otherwise
              // disappears into the background).
              return { backgroundColor: color, borderColor: chartTheme.swatch.border, borderWidth: 1 }
            },
          },
        },
      },
    }),
    [chartTheme]
  )
}
